import { Action, Dispatch } from 'redux';
import moment from 'moment';
import { IAppState } from './reducers';
import { INotification, getAll } from '../api/vault/notifications';

const NOTIFICATIONS_READ_STORAGE_KEY = 'catapult:notificationsRead';
const TIMEOUT_NOTIFICATION_READ_STORAGE_KEY = 'catapult:timeoutNotificationRead';

export interface IReadableNotification extends INotification {
  read: boolean;
}

export enum ActionType {
  LoadNotifications = 'Notifications.LoadNotifications',
  MarkRead = 'Notifications.MarkRead',
}

export interface INotificationsAction extends Action<ActionType> {
  payload?: any;
}

export interface IState {
  isLoaded: boolean;
  notifications: Array<IReadableNotification>,
}

const INITIAL_STATE: IState = {
  isLoaded: false,
  notifications: [],
};

export default function reducer(state: IState = INITIAL_STATE, action: INotificationsAction): IState {
  switch (action.type) {
    case ActionType.LoadNotifications:
      return {
        ...state,
        isLoaded: true,
        notifications: action.payload.notifications,
      };
    case ActionType.MarkRead:
      return {
        ...state,
        notifications: action.payload.notifications,
      };
    default:
      return state;
  }
}

export const loadNotifications = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  try {
    const response = await getAll();
    const notifications = response.data?.data || [];
    const timeoutNotification = markTimeoutNotification(state);
    if (timeoutNotification) {
      notifications.push(timeoutNotification);
    }
    let read: string | Array<string> = localStorage.getItem(NOTIFICATIONS_READ_STORAGE_KEY);
    read = read ? read.split(',') : [];
    dispatch({
      type: ActionType.LoadNotifications,
      payload: {
        notifications: notifications.reduce((container, notification) => {
          const { _id } = notification;
          return [
            ...container,
            {
              ...notification,
              read: read.includes(_id),
            },
          ];
        }, []),
      },
    });
  } catch (e) {
    console.error('Error fetching notifications: ', e.toString());
    dispatch({
      type: ActionType.LoadNotifications,
      payload: {
        notifications: [],
      },
    });
  }
};

export const markNotificationRead = (id: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  let read: string | Array<string> = localStorage.getItem(NOTIFICATIONS_READ_STORAGE_KEY);
  read = read ? read.split(',') : [];
  if (!read.includes(id)) {
    read = [...read, id];
  }
  if (id === 'activeTimeout') {
    localStorage.setItem(TIMEOUT_NOTIFICATION_READ_STORAGE_KEY, moment().add(24, 'hours').format('llll').toString());
  } else {
    localStorage.setItem(NOTIFICATIONS_READ_STORAGE_KEY, read.join(','));
  }
  const { notifications: { notifications } } = getState();
  dispatch({
    type: ActionType.MarkRead,
    payload: {
      notifications: (notifications || []).reduce((container, notification) => {
        const { _id } = notification;
        return [
          ...container,
          {
            ...notification,
            read: read.includes(_id),
          },
        ];
      }, []),
    },
  });
};

const markTimeoutNotification = (currentState: IAppState) => {
  const dayLater = moment().isBefore(localStorage.getItem(TIMEOUT_NOTIFICATION_READ_STORAGE_KEY));
  if (currentState?.exchange?.initExchange?.timeout && !dayLater) {
    const timeoutNotification: IReadableNotification = {
      _id: 'activeTimeout',
      title: 'Active Timeout',
      message: 'Access Restricted: Your access has been restricted due to missing game field uploads. You can upload content but can only download once all game parts are uploaded.',
      type: 2,
      products: [currentState.product.target.toString()],
      sports: [currentState.exchange.initExchange.sport],
      created: moment().toString(),
      read: false,
    };
    return timeoutNotification;
  }
  return null;
};
